/**
 * Payment API client for CardElement
 * Handles tokenization, payment submission, and status polling
 *
 * IMPORTANT: Assumes payment is pre-created with billing information.
 * Only sends paymentId + tokenIntentId to confirm endpoint.
 */

import type { CardData, PaymentResult, PaymentStatus } from '../../types';
import type { InternalSDKConfig, PaymentStatusResponse, ServerPaymentResponse } from './types';
import { ThreeDSModal } from './3ds-modal';
import { mapApiError, mapException, mapErrorCode } from './error-mapper';

export class PaymentClient {
  private threeDSModal: ThreeDSModal;

  constructor(
    private config: InternalSDKConfig,
    private paymentId: string
  ) {
    this.threeDSModal = new ThreeDSModal();
  }

  /**
   * Complete payment flow with tokenization and status polling
   * Payment must already be created with all billing information
   */
  async completePayment(cardData: CardData): Promise<PaymentResult> {
    this.log('[CardElement] Starting payment...', { paymentId: this.paymentId });

    try {
      // Step 1: Tokenize card
      this.log('[CardElement] Tokenizing card...');
      const tokenResponse = await this.tokenizeCard(cardData);
      this.log('[CardElement] Card tokenized', { tokenId: tokenResponse.tokenIntentId });

      // Step 2: Submit payment (with paymentId + tokenIntentId only)
      this.log('[CardElement] Submitting payment...');
      const paymentResponse = await this.submitPayment(tokenResponse.tokenIntentId);
      this.log('[CardElement] Payment response from /sdk/payment/confirm:', paymentResponse);

      // Step 3: Handle 3DS challenge with modal
      if (paymentResponse.threeDsSessionUrl) {
        this.log('[CardElement] 3DS challenge required, opening modal...');

        // Open 3DS modal and wait for completion
        const threeDSSuccess = await this.threeDSModal.open(paymentResponse.threeDsSessionUrl);

        this.log('[CardElement] 3DS modal closed:', threeDSSuccess ? 'success' : 'failure');

        // If 3DS failed or was canceled, return error
        if (!threeDSSuccess) {
          return {
            status: 'PAYMENT_FAILED' as PaymentStatus,
            error: {
              code: 'THREE_DS_FAILED',
              message: '3D Secure authentication failed or was canceled',
              retryable: true,
            },
          };
        }

        // 3DS completed successfully - now poll for final payment status
        this.log('[CardElement] 3DS completed, polling payment status...');
        try {
          const finalStatus = await this.pollUntilComplete();
          this.log('[CardElement] Polling complete, final status:', finalStatus);
          return finalStatus;
        } catch (pollError) {
          this.log('[CardElement] Error during polling:', pollError);
          return {
            status: 'PAYMENT_FAILED' as PaymentStatus,
            error: {
              code: 'POLLING_ERROR',
              message: 'Failed to verify payment status after 3DS',
              retryable: true,
            },
          };
        }
      }

      // Step 4: If payment is already complete, return
      if (this.isSuccessfulStatus(paymentResponse.status)) {
        return {
          status: paymentResponse.status,
          alreadyProcessed: paymentResponse.alreadyProcessed
        };
      }

      // Step 5: Wait for processing
      this.log('[CardElement] Waiting for processing...');
      await new Promise(resolve => setTimeout(resolve, 2500));

      // Step 6: Poll for status
      const maxPolls = 3;
      const pollInterval = 2000;

      for (let attempt = 1; attempt <= maxPolls; attempt++) {
        this.log(`[CardElement] Polling status (${attempt}/${maxPolls})...`);
        const status = await this.pollPaymentStatus();
        this.log('[CardElement] Full status response from /sdk/payment/:id:', status);
        this.log('[CardElement] Status response summary:', {
          status: status.status,
          hasError: !!status.error,
          hasLastDepositError: !!status.lastDepositAttempt?.error,
          hasChallengeUrl: !!status.challengeUrl
        });

        if (status.error) {
          this.log('[CardElement] Error details:', status.error);
        }
        if (status.lastDepositAttempt?.error) {
          this.log('[CardElement] Last deposit attempt error:', status.lastDepositAttempt.error);
        }

        // Success
        if (this.isSuccessfulStatus(status.status)) {
          return { status: status.status };
        }

        // Failed
        if (this.isFailedStatus(status.status)) {
          const errorCode = status.error || status.lastDepositAttempt?.error || 'PAYMENT_FAILED';
          return {
            status: status.status,
            error: {
              code: errorCode,
              message: mapErrorCode(errorCode),
              retryable: this.isRetryableErrorCode(errorCode),
            },
          };
        }

        // Check if there's an error in pending/processing states
        // This handles cases where payment failed but status hasn't updated yet
        const errorCode = status.error || status.lastDepositAttempt?.error;
        if (errorCode && (status.status === 'CHECKOUT_PENDING' || status.status === 'INITIATION')) {
          this.log('[CardElement] Found error in pending state, returning error:', errorCode);
          return {
            status: status.status,
            error: {
              code: errorCode,
              message: mapErrorCode(errorCode),
              retryable: this.isRetryableErrorCode(errorCode),
            },
          };
        }

        // Still processing - retry
        if (attempt < maxPolls) {
          await new Promise(resolve => setTimeout(resolve, pollInterval));
          continue;
        }

        // Timeout
        return {
          status: status.status,
          error: {
            code: 'PAYMENT_PROCESSING',
            message: 'Payment is still processing. Please check status later.',
            retryable: false,
          },
        };
      }

      throw new Error('Payment processing did not complete');
    } catch (error) {
      this.log('[CardElement] Payment error:', error);

      // Map exception to user-friendly error
      const mappedError = mapException(error, this.config.debug);
      return {
        status: 'PAYMENT_FAILED' as PaymentStatus,
        error: mappedError,
      };
    }
  }

  /**
   * Tokenize card via proxy (only card data, no billing info)
   */
  private async tokenizeCard(cardData: CardData): Promise<{ tokenIntentId: string }> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.config.tokenizationProxyKey) {
      headers['BT-PROXY-KEY'] = this.config.tokenizationProxyKey;
    }

    const response = await fetch(this.config.tokenizationProxyUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        card: {
          number: cardData.number,
          expiration_month: cardData.expiration_month,
          expiration_year: cardData.expiration_year,
          cvc: cardData.cvc,
        },
      }),
      signal: AbortSignal.timeout(this.config.timeout),
    });

    if (!response.ok) {
      const errorText = await response.text();
      this.log('[CardElement] Tokenization failed:', { status: response.status, body: errorText });
      let errorData: any = {};
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        // Not JSON, will be handled by error mapper
      }

      // Map API error to user-friendly message
      const mappedError = mapApiError(errorData, response.status, this.config.debug);
      throw new Error(mappedError.message);
    }

    const responseText = await response.text();
    const contentType = response.headers.get('content-type') || '';
    this.log('[CardElement] Tokenization response:', {
      status: response.status,
      contentType,
      body: responseText.substring(0, 500) // Log first 500 chars
    });

    if (!responseText || responseText.trim() === '') {
      const mappedError = mapApiError({}, response.status, this.config.debug);
      throw new Error(mappedError.message);
    }

    // Check if response is HTML instead of JSON (indicates proxy error)
    if (contentType.includes('text/html')) {
      this.log('[CardElement] Received HTML instead of JSON - proxy configuration issue');
      // Map this to a generic system error (don't expose proxy details)
      const mappedError = mapApiError({ message: 'Service temporarily unavailable' }, 503, this.config.debug);
      throw new Error(mappedError.message);
    }

    let parsedResponse: any;
    try {
      parsedResponse = JSON.parse(responseText);
    } catch (e) {
      this.log('[CardElement] Failed to parse response as JSON:', responseText);
      const mappedError = mapApiError({ message: 'Service temporarily unavailable' }, 500, this.config.debug);
      throw new Error(mappedError.message);
    }

    // Handle wrapped response format from Basis Theory proxy: { body: { tokenIntentId, ... }, headers: ... }
    if (parsedResponse.body && typeof parsedResponse.body === 'object') {
      this.log('[CardElement] Unwrapping proxy response body');
      parsedResponse = parsedResponse.body;
    }

    // Handle response format: { tokenIntent: { id: "..." }, ... } → { tokenIntentId: "..." }
    if (parsedResponse.tokenIntent && parsedResponse.tokenIntent.id) {
      this.log('[CardElement] Extracting tokenIntentId from tokenIntent.id');
      return {
        tokenIntentId: parsedResponse.tokenIntent.id,
        ...parsedResponse
      };
    }

    // Handle direct format: { tokenIntentId: "..." }
    if (parsedResponse.tokenIntentId) {
      return parsedResponse;
    }

    // If we got here, the response format is unexpected
    this.log('[CardElement] Unexpected tokenization response format:', parsedResponse);
    const mappedError = mapApiError({ message: 'Service temporarily unavailable' }, 500, this.config.debug);
    throw new Error(mappedError.message);
  }

  /**
   * Submit payment to API - simplified
   * Only sends paymentId + tokenIntentId (billing info already in payment)
   */
  private async submitPayment(tokenIntentId: string): Promise<ServerPaymentResponse> {
    // Generate callback URLs for 3DS flow
    // These point to API-hosted pages that communicate completion via postMessage
    const callbackBase = this.getCallbackBaseUrl();

    // SECURITY: URL-encode paymentId to prevent malformed URLs
    const encodedPaymentId = encodeURIComponent(this.paymentId);

    // Simplified payload matching new SdkConfirmPaymentDto
    const payload = {
      paymentId: this.paymentId,    // Payment pre-created with billing info
      tokenIntentId,                 // Token from Basis Theory
      threeDsSuccessUrl: `${callbackBase}/success?paymentId=${encodedPaymentId}`,
      threeDsFailureUrl: `${callbackBase}/failure?paymentId=${encodedPaymentId}`,
    };

    this.log('[CardElement] Payment payload:', payload);

    const response = await fetch(
      `${this.config.apiUrl}/sdk/payment/confirm`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Inflow-Public-Key': this.config.apiKey,
        },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(this.config.timeout),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      let errorData: any = {};
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        // Not JSON, will be handled by error mapper
      }

      // Special handling: If we get an error, check the actual payment status
      // This helps detect if payment was already processed successfully
      if (response.status === 400) {
        this.log('[CardElement] Got 400 error from /confirm, checking actual payment status...');
        try {
          const paymentStatus = await this.pollPaymentStatus();
          this.log('[CardElement] Actual payment status:', paymentStatus);

          // If payment is already in a successful state, return success with alreadyProcessed flag
          if (this.isSuccessfulStatus(paymentStatus.status)) {
            this.log('[CardElement] Payment already successful, returning success result with alreadyProcessed flag');
            return {
              paymentId: this.paymentId,
              status: paymentStatus.status,
              amount: paymentStatus.amount,
              amountTaxesIncluded: paymentStatus.amountTaxesIncluded,
              currency: paymentStatus.currency,
              depositStatus: paymentStatus.depositStatus,
              alreadyProcessed: true, // Flag to indicate this was a duplicate submission
            };
          }
        } catch (pollError) {
          this.log('[CardElement] Failed to poll payment status:', pollError);
          // Continue with original error handling
        }
      }

      // Map API error to user-friendly message (this handles your 3DS 424 case!)
      const mappedError = mapApiError(errorData, response.status, this.config.debug);
      throw new Error(mappedError.message);
    }

    return response.json();
  }

  /**
   * Poll payment status until we get a final result (success or failure)
   * Used after 3DS completion
   */
  private async pollUntilComplete(): Promise<PaymentResult> {
    const maxAttempts = 10;
    const interval = 2000;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      this.log(`[CardElement] Polling payment status (${attempt}/${maxAttempts})...`);

      try {
        const payment = await this.pollPaymentStatus();

        // Log full payment data in debug mode
        this.log(`[CardElement] Payment status response:`, payment);

        // Check for final success states
        if (this.isSuccessfulStatus(payment.status) || payment.depositStatus === 'succeeded') {
          return { status: payment.status };
        }

        // Check for failure states
        if (this.isFailedStatus(payment.status) || payment.depositStatus === 'failed') {
          const errorCode = payment.error || payment.lastDepositAttempt?.error || 'PAYMENT_FAILED';
          return {
            status: payment.status,
            error: {
              code: errorCode,
              message: mapErrorCode(errorCode),
              retryable: this.isRetryableErrorCode(errorCode),
            },
          };
        }

        // Check if there's an error in pending/processing states
        // This handles cases where payment failed but status hasn't updated yet
        const errorCode = payment.error || payment.lastDepositAttempt?.error;
        if (errorCode && (payment.status === 'CHECKOUT_PENDING' || payment.status === 'INITIATION')) {
          this.log('[CardElement] Found error in pending state:', errorCode);
          return {
            status: payment.status,
            error: {
              code: errorCode,
              message: mapErrorCode(errorCode),
              retryable: this.isRetryableErrorCode(errorCode),
            },
          };
        }

        // Still processing, wait and retry
        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, interval));
        }
      } catch (error) {
        this.log('[CardElement] Error polling status:', error);

        if (attempt === maxAttempts) {
          // Map error to user-friendly message
          const mappedError = mapException(error, this.config.debug);

          // Override with specific status code handling if available
          if ((error as any)?.statusCode) {
            const statusCode = (error as any).statusCode;
            const apiError = mapApiError({}, statusCode, this.config.debug);
            return {
              status: 'PAYMENT_FAILED' as PaymentStatus,
              error: apiError,
            };
          }

          return {
            status: 'PAYMENT_FAILED' as PaymentStatus,
            error: mappedError,
          };
        }
      }
    }

    // Timed out
    return {
      status: 'CHECKOUT_PENDING' as PaymentStatus,
      error: {
        code: 'PAYMENT_PROCESSING',
        message: 'Payment is still processing',
        retryable: false,
      },
    };
  }

  /**
   * Poll payment status
   */
  private async pollPaymentStatus(): Promise<PaymentStatusResponse> {
    const response = await fetch(
      `${this.config.apiUrl}/sdk/payment/${this.paymentId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Inflow-Public-Key': this.config.apiKey,
        },
        signal: AbortSignal.timeout(this.config.timeout),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      let errorData: any = {};
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        // Not JSON, will be handled by error mapper
      }

      // Map API error to user-friendly message
      const mappedError = mapApiError(errorData, response.status, this.config.debug);
      const error: any = new Error(mappedError.message);
      error.statusCode = response.status; // Attach status code for better error handling
      throw error;
    }

    return response.json();
  }

  /**
   * Check if status is successful
   */
  private isSuccessfulStatus(status: PaymentStatus): boolean {
    return ['CHECKOUT_SUCCESS', 'PAYMENT_RECEIVED', 'PAYMENT_SUCCESS'].includes(status);
  }

  /**
   * Check if status is failed
   */
  private isFailedStatus(status: PaymentStatus): boolean {
    return ['PAYMENT_FAILED', 'CHECKOUT_CANCELED', 'CANCELED'].includes(status);
  }

  /**
   * Check if error code is retryable
   */
  private isRetryableErrorCode(errorCode?: string | null): boolean {
    if (!errorCode) return false;

    const retryableCodes = [
      'invalid_card_number',
      'invalid_expiry',
      'invalid_cvc',
      'card_declined',
      'insufficient_funds',
      'network_error',
      'INVALID_CARD_NUMBER',
      'INVALID_EXPIRY',
      'INVALID_CVC',
      'CARD_DECLINED',
      'INSUFFICIENT_FUNDS',
      'NETWORK_ERROR',
      // Stripe-specific codes
      'requires_payment_method',
      'test_mode_live_card',
      'generic_decline',
      'lost_card',
      'stolen_card',
      'do_not_honor',
      'do_not_try_again',
    ];

    return retryableCodes.includes(errorCode);
  }

  /**
   * Get default API URL based on environment
   */
  static getDefaultApiUrl(environment: string): string {
    if (environment === 'production') {
      return 'https://api.inflowpay.xyz';
    } else if (environment === 'development') {
      return 'https://pre-prod.api.inflowpay.xyz';
    } else {
      // sandbox (local development)
      return 'http://localhost:3000';
    }
  }

  /**
   * Get default tokenization proxy URL based on environment
   */
  static getDefaultTokenizationProxyUrl(_environment: string): string {
    // All environments use Basis Theory proxy for tokenization
    return 'https://api.basistheory.com/proxy';
  }

  /**
   * Get default Basis Theory proxy key based on environment and API key
   */
  static getDefaultTokenizationProxyKey(environment: string, apiKey?: string): string | undefined {
    // Check if this is preprod (needs production BT key even though it uses development environment)
    const isPreprod = apiKey && (apiKey.includes('_preprod_') || apiKey.startsWith('inflow_preprod_'));

    if (environment === 'production' || isPreprod) {
      // Basis Theory production proxy key for production and preprod
      return 'key_prod_us_proxy_LfqTJPkDm8p55QtQf3BftJ';
    } else if (environment === 'development' || environment === 'sandbox') {
      // Basis Theory dev proxy key for development and local
      // return 'key_test_us_proxy_9xV4eL5Pn5c9AELTD7LbJf';
      return 'key_prod_us_proxy_LfqTJPkDm8p55QtQf3BftJ';
    } else {
      return undefined;
    }
  }

  /**
   * Get 3DS callback base URL based on environment
   * Returns API-hosted callback page URLs to prevent 404 errors
   */
  private getCallbackBaseUrl(): string {
    const apiUrl = this.config.apiUrl;

    // Use the API URL and append /threeds path
    // This ensures callbacks are hosted on the API server
    return `${apiUrl}/threeds`;
  }

  /**
   * Debug logging
   */
  private log(message: string, data?: any): void {
    if (this.config.debug) {
      if (data !== undefined) {
        console.log(message, data);
      } else {
        console.log(message);
      }
    }
  }
}